import { Box, Button, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import { formatDate } from "@aclymatepackages/formatters";

import Link from "../../../atoms/mui/Link";

import {
  useCollectionDataListener,
  useAccountData
} from "../../../../helpers/firebase";
import usePermissions from "../../../../helpers/hooks/usePermissions";
import {
  useShareCustomerYear,
  isVendorSurveyYearIncomplete
} from "../../../../helpers/components/vendors";

import CollectionScreen, { StatusChip } from "./CollectionScreen";
import { toMillis } from "../../views/dashboardHelpers";

const CUSTOMER_VIEW_HREF = (relationshipId) =>
  `/explorer/dashboard?view=customers&relationshipId=${relationshipId}`;

const STATUS_BY_SURVEY = {
  accepted: { label: "Accepted", tone: "success", action: "View" },
  pending: {
    label: "Shared — awaiting confirmation",
    tone: "info",
    action: "View"
  },
  unconfirmed: { label: "Share needed", tone: "pending", action: "Share" },
  requested: { label: "Awaiting you", tone: "pending", action: "Share" },
  revoked: { label: "Revoked", tone: "neutral", action: "Restore" }
};

const SHAREABLE_STATUSES = ["unconfirmed", "requested", "revoked"];

const isShareableStatus = (status) => SHAREABLE_STATUSES.includes(status);

const SHARE_ACTIONS = ["Share", "Restore"];

const isShareAction = (action) => SHARE_ACTIONS.includes(action);

const entryTimestamp = (entry) => toMillis(entry?.dateRequested || entry?.dateAccepted);

const sortSurveyEntriesByRecency = (entries) =>
  entries
    .slice()
    .sort((a, b) => entryTimestamp(b) - entryTimestamp(a) || (Number(b.year) || 0) - (Number(a.year) || 0));

export const buildCustomerRow = (customer, surveyEntry) => {
  const legacyStatus = customer.survey?.status;
  const status = surveyEntry?.status || legacyStatus || null;
  const config =
    STATUS_BY_SURVEY[status] || {
      label: "No requests",
      tone: "neutral",
      action: "View"
    };
  const year = surveyEntry?.year || customer.survey?.year || "—";
  const lastDate =
    surveyEntry?.dateAccepted ||
    surveyEntry?.dateRequested ||
    (surveyEntry ? null : customer.survey?.dateInvited) ||
    null;

  return {
    id: customer.id,
    rowKey: `${customer.id}-${year}`,
    name: customer.buyerName || "Unnamed customer",
    year,
    shareYear: surveyEntry?.year || customer.survey?.year || null,
    statusLabel: config.label,
    statusTone: config.tone,
    updated: lastDate ? formatDate(lastDate) : "—",
    action: config.action
  };
};

export const buildCustomerRows = (customer) => {
  const entries = customer.surveyData || [];
  if (!entries.length) {
    return [buildCustomerRow(customer, null)];
  }
  return sortSurveyEntriesByRecency(entries).map((entry) =>
    buildCustomerRow(customer, entry)
  );
};

const countByTone = (rows, tone) =>
  rows.filter((row) => row.statusTone === tone).length;

export const CustomerActionCell = ({ row }) => {
  const shareCustomerYear = useShareCustomerYear();
  const [accountData] = useAccountData();
  const { canShare } = usePermissions();

  if (!isShareAction(row.action)) {
    return (
      <Link href={CUSTOMER_VIEW_HREF(row.id)}>
        <Button color="primary" size="small">
          <Typography variant="caption" color="primary">
            {row.action}
          </Typography>
        </Button>
      </Link>
    );
  }

  if (!canShare) {
    return null;
  }

  const isIncomplete = isVendorSurveyYearIncomplete(
    accountData?.vendorSurveys || [],
    row.shareYear
  );

  const shareButton = (
    <Button
      color="primary"
      size="small"
      disabled={isIncomplete}
      onClick={() =>
        shareCustomerYear({ relationshipId: row.id, year: row.shareYear })
      }
    >
      <Typography variant="caption" color="primary">
        {row.action}
      </Typography>
    </Button>
  );

  if (isIncomplete) {
    return (
      <Tooltip title={`Your ${row.year} survey is incomplete — finish the skipped or unknown answers before sharing.`}>
        <span>{shareButton}</span>
      </Tooltip>
    );
  }

  return shareButton;
};

const COLUMNS = [
  { key: "name", label: "Customer" },
  { key: "year", label: "Reporting year" },
  {
    key: "status",
    label: "Status",
    render: (row) => <StatusChip label={row.statusLabel} tone={row.statusTone} />
  },
  { key: "updated", label: "Last activity" },
  {
    key: "action",
    label: "",
    align: "right",
    render: (row) => <CustomerActionCell row={row} />
  }
];

const CUSTOMERS_INDEX_HREF = "/explorer/dashboard?view=customers";

const buildDetailStatusLines = (customer) => {
  const entries = customer.surveyData || [];
  if (entries.length) {
    return entries
      .slice()
      .sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0))
      .map((entry) => {
        const config = STATUS_BY_SURVEY[entry.status] || {
          label: "No requests",
          tone: "neutral"
        };
        const date = entry.dateAccepted || entry.dateRequested;
        return {
          year: entry.year || "—",
          shareYear: entry.year,
          status: entry.status,
          label: config.label,
          tone: config.tone,
          updated: date ? formatDate(date) : "—"
        };
      });
  }
  const legacy = customer.survey;
  if (!legacy) {
    return [];
  }
  const config = STATUS_BY_SURVEY[legacy.status] || {
    label: "No requests",
    tone: "neutral"
  };
  return [
    {
      year: legacy.year || "—",
      shareYear: legacy.year,
      status: legacy.status,
      label: config.label,
      tone: config.tone,
      updated: legacy.dateInvited ? formatDate(legacy.dateInvited) : "—"
    }
  ];
};

const CustomerDetail = ({ customer }) => {
  const history = useHistory();
  const { palette } = useTheme();
  const shareCustomerYear = useShareCustomerYear();
  const [accountData] = useAccountData();
  const { canShare } = usePermissions();
  const vendorSurveys = accountData?.vendorSurveys || [];
  const statusLines = buildDetailStatusLines(customer);

  const buyerName = customer.buyerName || "this customer";

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 0 }}>
      <Box>
        <Button
          size="small"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={() => history.push(CUSTOMERS_INDEX_HREF)}
        >
          <Typography variant="caption" color="primary">
            Back to Customers
          </Typography>
        </Button>
      </Box>
      <Box>
        <Typography variant="overline" color="textSecondary">
          Customer
        </Typography>
        <Typography variant="h4" color="textPrimary">
          {customer.buyerName || "Unnamed customer"}
        </Typography>
      </Box>
      <Paper elevation={1} sx={{ p: 3, borderRadius: "8px" }}>
        <Typography variant="subtitle1" color="textPrimary">
          Request history
        </Typography>
        {statusLines.length === 0 && (
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              No requests on file yet.
            </Typography>
          </Box>
        )}
        {statusLines.length > 0 && (
          <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
            {statusLines.map((line, idx) => {
              const isIncomplete = isVendorSurveyYearIncomplete(
                vendorSurveys,
                line.shareYear
              );
              const shareButton = (
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  disabled={isIncomplete}
                  sx={{ color: palette.common.white }}
                  onClick={() =>
                    shareCustomerYear({
                      relationshipId: customer.id,
                      year: line.shareYear
                    })
                  }
                >
                  <Typography variant="caption" color="inherit">
                    {line.status === "revoked" ? "Restore" : "Share"} {line.year}{" "}
                    data with {buyerName}
                  </Typography>
                </Button>
              );
              return (
                <Box
                  key={`${line.year}-${idx}`}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Typography variant="body2" color="textPrimary">
                    Reporting year {line.year}
                  </Typography>
                  <StatusChip label={line.label} tone={line.tone} />
                  <Typography variant="caption" color="textSecondary">
                    Last activity: {line.updated}
                  </Typography>
                  {canShare && isShareableStatus(line.status) && isIncomplete && (
                    <Tooltip title={`Your ${line.year} survey is incomplete — finish the skipped or unknown answers before sharing.`}>
                      <span>{shareButton}</span>
                    </Tooltip>
                  )}
                  {canShare &&
                    isShareableStatus(line.status) &&
                    !isIncomplete &&
                    shareButton}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const CustomersTab = ({ customers }) => {
  const [fallbackCustomers] = useCollectionDataListener("customers");
  const safeCustomers = customers || fallbackCustomers || [];
  const location = useLocation();
  const focusedRelationshipId = new URLSearchParams(location.search).get(
    "relationshipId"
  );
  const focusedCustomer = focusedRelationshipId
    ? safeCustomers.find(({ id }) => id === focusedRelationshipId)
    : null;

  if (focusedCustomer) {
    return <CustomerDetail customer={focusedCustomer} />;
  }

  const rows = safeCustomers.flatMap(buildCustomerRows);

  const accepted = countByTone(rows, "success");
  const pendingShare = countByTone(rows, "info");
  const awaitingYou = countByTone(rows, "pending");

  const stats = [
    { label: "Total customers", value: safeCustomers.length, tone: "total" },
    { label: "Active shares", value: accepted, tone: "success" },
    { label: "Awaiting you", value: awaitingYou, tone: "warning" },
    { label: "Shared", value: pendingShare, tone: "neutral" }
  ];

  const statusSegments = [
    { label: "Accepted", value: accepted, tone: "success" },
    { label: "Awaiting you", value: awaitingYou, tone: "pending" },
    { label: "Shared", value: pendingShare, tone: "info" }
  ];

  return (
    <CollectionScreen
      heading="My Emissions Requests"
      subtitle="Respond to emissions requests from customers and track who you're sharing with."
      stats={stats}
      statusTitle="Customer request status"
      statusSegments={statusSegments}
      tableTitle="Customers"
      columns={COLUMNS}
      rows={rows}
      emptyText="No customer requests yet."
    />
  );
};

export default CustomersTab;
